/**
 * HAND EDITS: a move, a resize or a spacing change the user made with their own hands, written INTO the
 * artifact's source on the element it changes. The slide stays one document; the AI reads these values
 * where they apply and is told, by the marks, that they are the user's (ARTIFACT-LAYOUT-EDITING.md, v3).
 *
 * Two marks ride on an edited element:
 * - `data-eid="e3"`: a name tag, stamped the first time the element is edited, so a later write can find
 *   it again after the paths around it have moved.
 * - `data-hand="translate:0px 80px;width:560px"`: exactly what the user set, read back from the CSSOM so it
 *   matches the inline declaration byte for byte. It is the document's own record of intent: a value that
 *   no longer matches its declaration means something other than the user changed it.
 *
 * AN ELEMENT THE USER ADDED is the one kind a hand edit creates: a picture (Beka, 2026-09-25), a text box, a
 * circle or a square (2026-09-27), each carrying a third mark, `data-hand-add`, beside the other two. Its
 * whole placement is hand values (absolute, left, top, width, height), so without them it would be an
 * unplaced element in the flow; a shape's fill and corners are hand values too. The mark means nothing on its
 * own: it rides an element that already carries `data-eid` and `data-hand`.
 *
 * EVERY DECLARATION IS `!important`. The contract requires `animation-fill-mode: both`, and a filled
 * entrance beats a plain inline value (measured in Chrome, 2026-09-24): without the priority a hand width
 * on an animated card simply never shows.
 *
 * VALUES ARE PX, UNITLESS NUMBERS, KEYWORDS, TOKENS AND RGB ONLY. The validator's REJECT 4 and 5 regex the
 * whole document, attributes included, so a `%` or `rem` inside `data-hand` would reject a write the
 * user never meant to break. One exception: a corner radius may be a percentage (a circle's `50%`), which
 * neither reject reads (REJECT 4 is `font-size` alone).
 *
 * Same guarantees as `artifactEdit`: DOMParser in, one serialization out, never throws, null means keep
 * the string you have.
 */

import { ADDED_TEXT_MAX, ADDED_TEXT_STYLE } from './addedLook'
import {
  editArtifactDocument,
  inlineStyleOf,
  normalizeProp,
  resolveArtifactPath,
  sanitizeArtifactInline,
} from './artifactEdit'
import { isStoredImageUrl } from './artifactValidate'

export const EID_ATTR = 'data-eid'
export const HAND_ATTR = 'data-hand'
/** "The user added this element" ({@link insertHandElements}). Empty value; only meaningful with the other two. */
export const ADD_ATTR = 'data-hand-add'

export const EID_PATTERN = /^e[0-9]{1,6}$/

/**
 * The properties a hand edit may set. Layout, spacing and a little look; never type, never motion. `display`,
 * `flex-direction` and the margins arrive with a move in a stack (Beka, 2026-09-25): a stack spaced by its
 * children's margins becomes a flex column spaced by its gap, so a reorder keeps the gaps even.
 */
export const HAND_PROPS: ReadonlySet<string> = new Set([
  'translate', 'width', 'height', 'min-height', 'position', 'left', 'top', 'right', 'bottom', 'z-index',
  // On the parent of an element sent behind the slide's text (layer order, 2026-09-27): its own stacking
  // context, so a negative z-index stays above the parent's background instead of vanishing behind it.
  'isolation',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'display', 'flex-direction',
  'gap', 'row-gap', 'column-gap', 'justify-content', 'align-items', 'align-self',
  'opacity', 'background-color', 'border-radius', 'border-width', 'border-style', 'border-color',
  'object-fit',
])

/**
 * What a hand edit may set on `<body>`: the slide's background, for an artifact with no full-slide wrapper
 * to carry it, and `isolation` (2026-09-27), when something added on body is sent behind the slide's text: body
 * then paints its own background under the negative z-index instead of over it. Body IS the slide, 1920x1080
 * by contract, so nothing about its box, place or order is the user's to change.
 */
export const BODY_HAND_PROPS: ReadonlySet<string> = new Set(['background-color', 'isolation'])

export type HandTarget = { path: number[] } | { eid: string }

export interface HandDecl {
  prop: string
  /** Null clears the property and drops it from the mark. */
  value: string | null
}

export interface HandEditResult {
  html: string
  eid: string
  /** Where the element sits in the RETURNED document. */
  path: number[]
}

export interface HandEditEntry {
  eid: string
  tag: string
  /** A short, URL-free label for prompts: the element's own words, or its first class. */
  label: string
  props: string[]
  /** A picture the user added ({@link ADD_ATTR}). Absent otherwise. */
  added?: boolean
}

const FUNCTIONAL_TOKEN = /var\(--a-[a-z0-9-]+\)|rgba?\([0-9.,\s%/]+\)/g
const PLAIN_TOKEN = /^(-?[0-9]*\.?[0-9]+px|-?[0-9]*\.?[0-9]+|[a-z][a-z-]*)$/
const PERCENT_TOKEN = /^[0-9]*\.?[0-9]+%$/

/** The one hand prop that may be a percentage: a circle's corners are `50%` whatever size it is resized to. */
const PERCENT_PROPS: ReadonlySet<string> = new Set(['border-radius'])

/**
 * True for a value the validator will never trip on: px, unitless numbers, keywords, tokens, rgb(). Given the
 * prop, a corner radius may also be a plain percentage ({@link PERCENT_PROPS}).
 */
export function isHandValue(value: string, prop?: string): boolean {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (trimmed === '' || /[;:]/.test(trimmed)) return false
  const rest = trimmed.replace(FUNCTIONAL_TOKEN, ' ')
  const percent = prop !== undefined && PERCENT_PROPS.has(prop)
  return rest
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => PLAIN_TOKEN.test(token) || (percent && PERCENT_TOKEN.test(token)))
}

/** `"translate:0px 80px;width:560px"` → Map. Unknown or malformed entries are dropped, not guessed at. */
export function parseHand(attr: string | null | undefined): Map<string, string> {
  const out = new Map<string, string>()
  if (typeof attr !== 'string') return out
  for (const entry of attr.split(';')) {
    const colon = entry.indexOf(':')
    if (colon <= 0) continue
    const name = normalizeProp(entry.slice(0, colon))
    const value = entry.slice(colon + 1).trim()
    if (name && HAND_PROPS.has(name) && value) out.set(name, value)
  }
  return out
}

export function formatHand(hand: Map<string, string>): string {
  return [...hand].map(([name, value]) => `${name}:${value}`).join(';')
}

function pathOfElement(el: Element): number[] | null {
  const out: number[] = []
  let node: Element = el
  const body = el.ownerDocument?.body
  while (node !== body) {
    const parent = node.parentElement
    if (!parent) return null
    out.unshift(Array.prototype.indexOf.call(parent.children, node))
    node = parent
  }
  return out
}

export function isBody(el: Element): boolean {
  return el.tagName.toUpperCase() === 'BODY'
}

/**
 * Hand edits stop at the structural roots, at `deck-*` components (sealed) and inside inline SVG. Body is
 * the one root they reach, and only for {@link BODY_HAND_PROPS}: see {@link isHandPropFor}.
 */
export function isHandEditable(el: Element): boolean {
  const tag = el.tagName.toUpperCase()
  if (tag === 'HTML' || tag === 'HEAD' || tag === 'SCRIPT' || tag === 'STYLE') return false
  for (let up = el.parentElement; up; up = up.parentElement) {
    const name = up.tagName.toUpperCase()
    if (name.startsWith('DECK-') || name === 'SVG') return false
  }
  return true
}

/** Tags that are never slide content, so the slide chain looks past them. */
const NON_CONTENT = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'TITLE', 'TEMPLATE', 'NOSCRIPT', 'BASE'])

/** Whether `el` carries {@link ADD_ATTR}: an element the user added by hand (a picture, a text box, a shape). */
export function isHandAdded(el: Element): boolean {
  return el.hasAttribute(ADD_ATTR)
}

/**
 * A child the slide chain looks past when it counts: a tag that is never content, or a picture the user
 * added, so adding one never changes which element is the slide. Nothing else, an empty layer included:
 * the bridge also demands that a frame fill the slide, which the source cannot measure, and without that
 * test a centred card beside an empty glow (or text beside a chart div a script fills) would become "the
 * slide". An empty sibling stops the chain at body, the safe side.
 */
function passedOver(kid: Element): boolean {
  return NON_CONTENT.has(kid.tagName.toUpperCase()) || isHandAdded(kid)
}

/**
 * Body, then its single-content-child chain (body's only content child, and its only content child, and so
 * on down), such as `<main class="frame">` holding everything. It is the source's stand-in for the bridge's
 * `slideTarget`, which has layout to measure and the source does not. Like the bridge, the chain only steps
 * into a child that itself holds elements, so the one headline on an otherwise empty slide is not the slide.
 */
function slideChain(doc: Document): Element[] {
  const chain: Element[] = []
  let node: Element | null = doc.body ?? null
  while (node) {
    chain.push(node)
    const kids: Element[] = Array.from(node.children).filter((kid) => !passedOver(kid))
    const only: Element | null = kids.length === 1 ? kids[0] : null
    node = only && only.children.length > 0 && isHandEditable(only) ? only : null
  }
  return chain
}

/** The slide's own element: the end of the slide chain, the frame when there is one, else body. */
export function slideElementOf(doc: Document): Element {
  const chain = slideChain(doc)
  return chain[chain.length - 1] ?? doc.documentElement
}

/** Whether `el` is the slide itself: body, or an element on the slide chain ({@link slideElementOf}). */
export function isSlideElement(el: Element): boolean {
  const doc = el.ownerDocument
  return !!doc && slideChain(doc).includes(el)
}

/** Whether a hand edit may set `prop` (normalised) on `el`: any hand prop, and on body only its own. */
export function isHandPropFor(el: Element, prop: string): boolean {
  return HAND_PROPS.has(prop) && (!isBody(el) || BODY_HAND_PROPS.has(prop))
}

/**
 * The element's mark, parsed. On body only {@link BODY_HAND_PROPS} are read: no hand edit can set anything
 * else there, so any other entry is not the user's and says nothing about what they want.
 */
export function handOf(el: Element): Map<string, string> {
  const hand = parseHand(el.getAttribute(HAND_ATTR))
  for (const name of [...hand.keys()]) if (!isHandPropFor(el, name)) hand.delete(name)
  return hand
}

/**
 * Body itself when it matches `selector`, then its matching descendants, in document order. Every scan
 * for marks goes through here, because `body.querySelectorAll` never returns body and a hand-edited body
 * would otherwise be invisible to every one of them.
 */
export function queryBodyAll(doc: Document, selector: string): Element[] {
  const body = doc.body
  if (!body) return []
  const inside = Array.from(body.querySelectorAll(selector))
  return body.matches(selector) ? [body, ...inside] : inside
}

/** `resolveArtifactPath`, except that the empty path is body itself, the slide's own hand target. */
function resolvePath(doc: Document, path: number[]): Element | null {
  if (Array.isArray(path) && path.length === 0) return doc.body ?? null
  return resolveArtifactPath(doc, path)
}

function resolveTarget(doc: Document, target: HandTarget): Element | null {
  if ('path' in target) return resolvePath(doc, target.path)
  if (!EID_PATTERN.test(target.eid)) return null
  const found = queryBodyAll(doc, `[${EID_ATTR}="${target.eid}"]`)
  return found.length === 1 ? found[0] : null
}

/** The next unused name tag in this document. */
export function nextEid(doc: Document): string {
  let max = 0
  queryBodyAll(doc, `[${EID_ATTR}]`).forEach((el) => {
    const m = /^e([0-9]+)$/.exec(el.getAttribute(EID_ATTR) ?? '')
    if (m) max = Math.max(max, Number(m[1]))
  })
  return `e${max + 1}`
}

/** Takes off a `style=""` a removal left empty. The attribute only, never the element, so body is safe. */
export function dropHusk(el: Element): void {
  const attr = el.getAttribute('style')
  if (attr !== null && attr.trim() === '') el.removeAttribute('style')
}

/**
 * Sets (or with a null value clears) hand declarations on one element, stamps its name tag on demand and
 * rewrites its `data-hand` from the values the CSSOM actually kept. Every declaration in `decls` lands or
 * none does: a property outside {@link HAND_PROPS} (outside {@link BODY_HAND_PROPS} on body, reached by
 * the empty path or its name tag), a value outside {@link isHandValue}, or a value the CSSOM refused (read
 * back empty) refuses the whole edit.
 */
export function applyHandEdit(html: string, target: HandTarget, decls: HandDecl[]): HandEditResult | null {
  if (!Array.isArray(decls) || decls.length === 0) return null
  const box: { eid: string; path: number[] } = { eid: '', path: [] }
  const next = editArtifactDocument(html, (doc) => {
    const el = resolveTarget(doc, target)
    if (!el || !isHandEditable(el)) return false
    const style = inlineStyleOf(el)
    if (!style) return false
    const hand = handOf(el)
    for (const decl of decls) {
      const name = normalizeProp(decl.prop)
      if (!name || !isHandPropFor(el, name)) return false
      if (decl.value === null) {
        style.removeProperty(name)
        hand.delete(name)
        continue
      }
      if (!isHandValue(decl.value, name)) return false
      style.setProperty(name, decl.value.trim(), 'important')
      const back = style.getPropertyValue(name).trim()
      if (!back || style.getPropertyPriority(name) !== 'important' || !isHandValue(back, name)) return false
      hand.set(name, back)
    }
    const existing = el.getAttribute(EID_ATTR)
    const eid = existing && EID_PATTERN.test(existing) ? existing : nextEid(doc)
    el.setAttribute(EID_ATTR, eid)
    if (hand.size > 0) el.setAttribute(HAND_ATTR, formatHand(hand))
    else el.removeAttribute(HAND_ATTR)
    dropHusk(el)
    const path = pathOfElement(el)
    if (!path) return false
    box.eid = eid
    box.path = path
    return true
  })
  return next ? { html: next, eid: box.eid, path: box.path } : null
}

/**
 * What makes an added picture a placed picture. A reset never takes these off one: the AI's CSS has
 * nothing for an element it never wrote, so the picture would fall into the flow at its natural size,
 * often thousands of pixels wide. The z-index the insert raised it by is placement too: without it the
 * picture paints under a z-indexed container, where a click can no longer reach it to select or delete it.
 * Removing the picture is Delete ({@link removeHand}).
 */
export const ADDED_PLACEMENT_PROPS: ReadonlySet<string> = new Set([
  'position', 'left', 'top', 'width', 'height', 'object-fit', 'z-index',
])

/** What an element the user added is: a picture, a text box (it has words) or a shape (a circle, a square). */
export type AddedKind = 'image' | 'text' | 'shape'

/** Which kind an added element is, the way the Properties panel tells them apart: by tag, then by words. */
export function addedKindOf(el: Element): AddedKind {
  if (el.tagName.toUpperCase() === 'IMG') return 'image'
  return (el.textContent ?? '').trim() ? 'text' : 'shape'
}

const SHAPE_KEEPS: ReadonlySet<string> = new Set([...ADDED_PLACEMENT_PROPS, 'background-color', 'border-radius'])

/**
 * What a reset never takes off an added element: its placement ({@link ADDED_PLACEMENT_PROPS}), and on a
 * shape its fill and its corners too. Those ARE the shape: without them a circle is an invisible box.
 */
export function addedKeepProps(kind: AddedKind): ReadonlySet<string> {
  return kind === 'shape' ? SHAPE_KEEPS : ADDED_PLACEMENT_PROPS
}

/**
 * Takes the user's hand off an element: removes the listed props (all of its hand props when `props` is
 * omitted) from both the inline style and the mark. With nothing left, the mark and the name tag go too,
 * and the element falls back to the AI's own CSS. On an added element what it is stays
 * ({@link addedKeepProps}), and so do its marks; with nothing else to take off, the reset is refused.
 */
export function resetHand(html: string, target: HandTarget, props?: string[]): HandEditResult | null {
  const box: { eid: string; path: number[] } = { eid: '', path: [] }
  const next = editArtifactDocument(html, (doc) => {
    const el = resolveTarget(doc, target)
    if (!el) return false
    const hand = handOf(el)
    if (hand.size === 0) return false
    const style = inlineStyleOf(el)
    const added = isHandAdded(el)
    const asked = props ? props.map((p) => normalizeProp(p)).filter((p): p is string => !!p) : [...hand.keys()]
    const keeps = added ? addedKeepProps(addedKindOf(el)) : null
    const names = keeps ? asked.filter((name) => !keeps.has(name)) : asked
    if (added && !names.some((name) => hand.has(name))) return false
    for (const name of names) {
      if (!hand.has(name)) continue
      style?.removeProperty(name)
      hand.delete(name)
    }
    box.eid = el.getAttribute(EID_ATTR) ?? ''
    if (hand.size > 0) el.setAttribute(HAND_ATTR, formatHand(hand))
    else {
      el.removeAttribute(HAND_ATTR)
      if (!added) el.removeAttribute(EID_ATTR)
    }
    dropHusk(el)
    const path = pathOfElement(el)
    if (!path) return false
    box.path = path
    return true
  })
  return next ? { html: next, eid: box.eid, path: box.path } : null
}

/**
 * Moves the element at `path` to `toIndex` among its element siblings: a real DOM move, so the new order
 * is simply the slide's order and the AI reads it as such. Undo reverts it; there is no mark for order.
 * Every sibling path after the lower of the two indices changes, so the caller re-selects by the returned
 * path. Never body: it is the slide, with nothing to be ordered among.
 */
export function moveHand(html: string, path: number[], toIndex: number): { html: string; path: number[] } | null {
  const box: { path: number[] } = { path: [] }
  const next = editArtifactDocument(html, (doc) => {
    // `resolveArtifactPath` already answers null for the empty path; the body check says so on purpose.
    const el = resolveArtifactPath(doc, path)
    if (!el || isBody(el) || !isHandEditable(el)) return false
    const parent = el.parentElement
    if (!parent) return false
    const others = Array.from(parent.children).filter((kid) => kid !== el)
    if (!Number.isInteger(toIndex) || toIndex < 0 || toIndex > others.length) return false
    if (Array.prototype.indexOf.call(parent.children, el) === toIndex) return false
    parent.insertBefore(el, others[toIndex] ?? null)
    const moved = pathOfElement(el)
    if (!moved) return false
    box.path = moved
    return true
  })
  return next ? { html: next, path: box.path } : null
}

/**
 * Tags the live bridge never offers as a drop container, so never a parent here either. Words and their
 * runs (the bridge's INLINE_TAGS, tag for tag): a block inside a `<p>` or an `<a>` is re-homed by the next
 * parse, and a run has no box to lay children out in. Controls, replaced and void elements, whose children
 * never render or never survive serialization. Foreign content (`svg`, `math`) and the tags that are never
 * slide content. `deck-*` is a prefix, tested apart.
 */
const NOT_A_CONTAINER: ReadonlySet<string> = new Set([
  'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BUTTON', 'LABEL', 'LEGEND', 'SUMMARY', 'CAPTION',
  'OPTION', 'OPTGROUP', 'SELECT', 'TEXTAREA', 'INPUT',
  'SPAN', 'A', 'B', 'I', 'EM', 'STRONG', 'U', 'S', 'STRIKE', 'SMALL', 'SUP', 'SUB', 'MARK', 'CODE', 'ABBR',
  'CITE', 'Q', 'VAR', 'KBD', 'SAMP', 'TIME', 'FONT', 'BDI', 'BDO', 'INS', 'DEL',
  'IMG', 'PICTURE', 'VIDEO', 'AUDIO', 'CANVAS', 'IFRAME', 'OBJECT', 'EMBED', 'SOURCE', 'TRACK', 'AREA',
  'HR', 'BR', 'WBR', 'COL', 'PARAM', 'SVG', 'MATH',
  ...NON_CONTENT,
])

/**
 * Where a moved element may land: a box a hand edit reaches, the bridge would offer, and no parse re-homes.
 * Never into something the user added: a text box or a shape is one thing, placed on top, not a container
 * (the bridge never offers one as a drop).
 */
function canHoldMoved(parent: Element): boolean {
  const tag = parent.tagName.toUpperCase()
  if (!isHandEditable(parent) || tag.startsWith('DECK-') || NOT_A_CONTAINER.has(tag) || isHandAdded(parent)) return false
  // Anywhere inside words, a link, a button or a form: a moved block there is re-homed by the parser (a
  // second form is dropped outright) or becomes part of the control. The bridge refuses the same containers.
  return !parent.parentElement?.closest('p, a, button, form')
}

/**
 * The tree under body as one string: each element's depth and tag, in document order. Two parses with the
 * same shape address every element by the same path. Depth counts as well as order: a link inside a link,
 * or a list item inside another item's div, re-parses as the same tags in the same order, one level up.
 */
function shapeOf(doc: Document): string | null {
  const body = doc.body
  if (!body) return null
  return Array.from(body.querySelectorAll('*'), (el) => {
    let depth = 0
    for (let up = el.parentElement; up && up !== body; up = up.parentElement) depth++
    return `${depth}:${el.tagName}`
  }).join(' ')
}

/**
 * Moves the element at `path` into the element at `parentPath` (`[]` is body), before the child at
 * `beforePath`, or last when that is null: the drop of a drag in a container (Beka, 2026-09-25: "they need
 * to reorder by dragging", and into other containers, like Figma). A real DOM move like {@link moveHand},
 * so the new place is simply the slide's and the marks travel with the element. All three paths are read
 * in THIS document, before anything moves, the way the bridge reports them. Returns where the element sits
 * in the returned document.
 *
 * Refused (null):
 * - an element that is body, on the slide chain, or out of a hand edit's reach;
 * - a parent that is the element or inside it, that {@link canHoldMoved} refuses, or that is body while a
 *   frame is the slide;
 * - a `before` that is not the parent's child, or is the element;
 * - a move that changes nothing.
 *
 * Two checks on the result:
 * - THE SAME SLIDE ELEMENT (P4). Which element is the slide decides what may be moved or deleted and where
 *   an added picture goes. Moving the frame's last other child into its sibling, or putting one beside the
 *   frame's only child, would quietly promote a card to the slide, or demote the frame to a movable box.
 * - THE SAME TREE THROUGH A RE-PARSE (P3). The iframe parses this source afresh, and a block the parser
 *   re-homes (into a `p`, a link into a link, a list item into another's div, a table row outside its
 *   table) would leave the returned path, and every path after it, naming something else there.
 */
export function moveHandTo(
  html: string,
  path: number[],
  parentPath: number[],
  beforePath: number[] | null,
  slidePath: number[] | null = null,
): { html: string; path: number[] } | null {
  if (!Array.isArray(path) || !Array.isArray(parentPath) || !(beforePath === null || Array.isArray(beforePath))) return null
  const box: { path: number[]; shape: string | null } = { path: [], shape: null }
  const next = editArtifactDocument(html, (doc) => {
    const el = resolveArtifactPath(doc, path)
    const parent = resolvePath(doc, parentPath)
    const before = beforePath === null ? null : resolveArtifactPath(doc, beforePath)
    const measured = measuredSlide(doc, slidePath)
    if (!el || isBody(el) || isTheSlide(el, measured) || !isHandEditable(el)) return false
    if (!parent || parent === el || el.contains(parent) || !canHoldMoved(parent)) return false
    const slide = measured ?? slideElementOf(doc)
    if (isBody(parent) && parent !== slide) return false
    if (beforePath !== null && (!before || before.parentElement !== parent || before === el)) return false
    if (el.parentElement === parent && (before ? el.nextElementSibling === before : parent.lastElementChild === el)) return false
    parent.insertBefore(el, before)
    // P4 on the source's own chain. With the frame's measured slide there is nothing to recheck: the slide is
    // what fills the frame, and neither it nor anything holding it is ever the one that moves.
    if (!measured && slideElementOf(doc) !== slide) return false
    const moved = pathOfElement(el)
    if (!moved) return false
    box.path = moved
    box.shape = shapeOf(doc)
    return true
  })
  if (!next || box.shape === null) return null
  try {
    const again = shapeOf(new DOMParser().parseFromString(next, 'text/html'))
    return again === box.shape ? { html: next, path: box.path } : null
  } catch {
    return null
  }
}

/* ── pictures the user adds ─────────────────────────────────────────────────────────────────────── */

/** A pool id as `data-deck-image` carries it: a plain token, safe inside an attribute selector unescaped. */
export const DECK_IMAGE_ID = /^[A-Za-z0-9_-]{1,128}$/

/** At most this many pictures in one add (a drop of several files is one write). */
const MAX_ADDED = 10
const ALT_MAX = 120

/**
 * Alt text an artifact can carry safely: no `<`, `>` or `"`, no line breaks or control characters, one
 * line of at most 120 characters, cut at a word when there is one in the second half. A `>` would end
 * `DECK_IMG_RE` (inlineDeckImages.ts) inside the attribute, and that picture would never be inlined.
 */
export function cleanAlt(text: string): string {
  if (typeof text !== 'string') return ''
  const flat = text.replace(/[<>"]/g, '').replace(/[\p{Cc}\p{Zl}\p{Zp}]/gu, ' ').replace(/\s+/g, ' ').trim()
  if (flat.length <= ALT_MAX) return flat
  const space = flat.lastIndexOf(' ', ALT_MAX)
  return (space >= ALT_MAX / 2 ? flat.slice(0, space) : flat.slice(0, ALT_MAX)).trim()
}

export interface HandImageItem {
  kind?: 'image'
  /** The pool id, written as `data-deck-image`. */
  id: string
  /** A stored URL (`/api/v1/storage/…`) for the source; a `data:image/` URI only inside the live frame. */
  src: string
  alt: string
  /** The placement, every value set: position absolute, left, top, width, height; object-fit, z-index. */
  decls: HandDecl[]
}

/**
 * A text box the user drew: a `<div>` of words in the deck's body type ({@link ADDED_TEXT_STYLE}). The dock's
 * opens empty in the frame alone and comes here once words were typed into it, as `html`.
 */
export interface HandTextItem {
  kind: 'text'
  /** Its words as plain text, at most {@link ADDED_TEXT_MAX} characters: a placeholder. */
  text?: string
  /**
   * Its words as the caret left them, inline markup and all (a `<br>` where a line broke, a bold run),
   * sanitized on the way in as every caret commit is ({@link sanitizeArtifactInline}). Wins over `text`.
   */
  html?: string
  /** The placement: position absolute, left, top; a width when one was drawn; z-index. */
  decls: HandDecl[]
}

/** A circle or a square the user drew: an empty `<div>` whose fill and corners are hand values too. */
export interface HandShapeItem {
  kind: 'shape'
  /** The placement, every value set, plus `background-color` and, on a circle, `border-radius`. */
  decls: HandDecl[]
}

export type HandAddItem = HandImageItem | HandTextItem | HandShapeItem

/**
 * The props each kind must arrive with: without them it is not placed anywhere. A text box has no height of
 * its own, since it grows with its words, and its width is optional: with none it grows as they are typed.
 */
const REQUIRED_PLACEMENT: Record<AddedKind, readonly string[]> = {
  image: ['position', 'left', 'top', 'width', 'height'],
  shape: ['position', 'left', 'top', 'width', 'height'],
  text: ['position', 'left', 'top'],
}

/**
 * Where a picture may be added: an element a hand edit reaches, and one that can hold an `<img>` through a
 * re-parse. Never inside a sealed `deck-*` component or an `<svg>` (an HTML `<img>` breaks out of foreign
 * content when the source is parsed again). Anything else a parse would move is caught by the round trip
 * in {@link insertHandElements}.
 */
function canHoldAdded(el: Element): boolean {
  const tag = el.tagName.toUpperCase()
  return isHandEditable(el) && !tag.startsWith('DECK-') && tag !== 'SVG' && !isHandAdded(el)
}

/**
 * Where a picture added with no parent goes: the slide's own element, or the nearest element above it that
 * can hold one ({@link canHoldAdded}), else body. The slide chain can end ON a full-bleed `<svg>` or a
 * `deck-*` (it steps into any only child that holds elements), and an `<img>` appended there never renders.
 * The live bridge's insert makes the same choice by walking down instead: body, then into the only content
 * child (added pictures aside) while it holds elements, is hand-editable and is not an svg or a `deck-*`.
 * Nothing below an svg or a `deck-*` is hand-editable, so the chain can only overshoot that walk by the one
 * element this steps back from.
 */
export function addParentOf(doc: Document): Element {
  let el: Element | null = slideElementOf(doc)
  while (el && !isBody(el) && !canHoldAdded(el)) el = el.parentElement
  return el ?? doc.body
}

/** The element an item makes, not yet marked or placed. Null for an item that is not what its kind needs. */
function createAdded(doc: Document, item: HandAddItem): Element | null {
  if (item.kind === 'text') {
    const div = doc.createElement('div')
    if (typeof item.html === 'string') {
      div.innerHTML = sanitizeArtifactInline(item.html)
    } else {
      const text = typeof item.text === 'string' ? item.text.trim() : ''
      if (text.length > ADDED_TEXT_MAX) return null
      div.textContent = text
    }
    // A text box with no words is nothing anyone can see or select: it never reaches the source.
    return (div.textContent ?? '').trim() ? div : null
  }
  if (item.kind === 'shape') return doc.createElement('div')
  if (typeof item.id !== 'string' || !DECK_IMAGE_ID.test(item.id)) return null
  const src = typeof item.src === 'string' ? item.src.trim() : ''
  if (/[\s<>"]/.test(src) || !(isStoredImageUrl(src) || /^data:image\//i.test(src))) return null
  const img = doc.createElement('img')
  img.setAttribute('data-deck-image', item.id)
  img.setAttribute('src', src)
  img.setAttribute('alt', cleanAlt(item.alt))
  return img
}

/** One new element, marked and placed, appended at the end of `parent`. Null refuses the whole batch. */
function addElement(doc: Document, parent: Element, item: HandAddItem): Element | null {
  if (!item || !Array.isArray(item.decls) || item.decls.length === 0) return null
  const kind: AddedKind = item.kind ?? 'image'
  const el = createAdded(doc, item)
  if (!el) return null
  el.setAttribute(ADD_ATTR, '')
  el.setAttribute(EID_ATTR, nextEid(doc))
  parent.appendChild(el)
  const style = inlineStyleOf(el)
  if (!style) return null
  // The same path applyHandEdit takes: every value is read back from the CSSOM, and the mark is what it kept.
  const hand = new Map<string, string>()
  for (const decl of item.decls) {
    const name = normalizeProp(decl?.prop)
    if (!name || !isHandPropFor(el, name) || typeof decl.value !== 'string' || !isHandValue(decl.value, name)) return null
    style.setProperty(name, decl.value.trim(), 'important')
    const back = style.getPropertyValue(name).trim()
    if (!back || style.getPropertyPriority(name) !== 'important' || !isHandValue(back, name)) return null
    hand.set(name, back)
  }
  if (hand.get('position') !== 'absolute' || REQUIRED_PLACEMENT[kind].some((name) => !hand.has(name))) return null
  // A text box's type is plain inline, the way the text bar writes type: not a hand value, never marked.
  if (kind === 'text') for (const [name, value] of ADDED_TEXT_STYLE) style.setProperty(name, value)
  el.setAttribute(HAND_ATTR, formatHand(hand))
  return el
}

/**
 * Adds elements the user placed by hand: a picture is an `<img>` with its pool id, its src and a clean alt; a
 * text box a `<div>` of words in the deck's body type; a circle or a square an empty `<div>`. Each carries the
 * three marks and its placement as hand values, appended at the END of the parent, after every child it has
 * (author scripts included). The live bridge inserts before its own injected scripts, which the source never
 * contains, so the paths agree. `parentPath` null is the slide's own element, or the nearest one above it
 * that can hold one ({@link addParentOf}); `[]` is body. One to ten items; any refusal (an id, a src, words, a
 * value, a parent that cannot hold them) refuses the whole batch. Returns each element's path in the returned
 * document and its name tag, in item order.
 */
export function insertHandElements(
  html: string,
  parentPath: number[] | null,
  items: HandAddItem[],
): { html: string; paths: number[][]; eids: string[] } | null {
  if (!Array.isArray(items) || items.length < 1 || items.length > MAX_ADDED) return null
  const box: { paths: number[][]; eids: string[] } = { paths: [], eids: [] }
  const next = editArtifactDocument(html, (doc) => {
    const parent = parentPath === null ? addParentOf(doc) : resolvePath(doc, parentPath)
    if (!parent || !canHoldAdded(parent)) return false
    const added: Element[] = []
    for (const item of items) {
      const el = addElement(doc, parent, item)
      if (!el) return false
      added.push(el)
    }
    for (const el of added) {
      const path = pathOfElement(el)
      if (!path) return false
      box.paths.push(path)
      box.eids.push(el.getAttribute(EID_ATTR) ?? '')
    }
    return true
  })
  if (!next) return null
  // The frame parses this source afresh; a parent whose children a parse moves or drops would give paths
  // the bridge cannot agree with. Checked once, on the same parser the rest of the edit uses.
  try {
    const again = new DOMParser().parseFromString(next, 'text/html')
    const same = box.paths.every((path, i) => resolveArtifactPath(again, path)?.getAttribute(EID_ATTR) === box.eids[i])
    return same ? { html: next, paths: box.paths, eids: box.eids } : null
  } catch {
    return null
  }
}

/** Pictures only: {@link insertHandElements} with every item an image. */
export function insertHandImages(
  html: string,
  parentPath: number[] | null,
  items: HandImageItem[],
): { html: string; paths: number[][]; eids: string[] } | null {
  if (!Array.isArray(items) || items.some((item) => item?.kind !== undefined && item.kind !== 'image')) return null
  return insertHandElements(html, parentPath, items)
}

/* ── copies the user makes ──────────────────────────────────────────────────────────────────────── */

/**
 * What a copy carries (Beka, 2026-09-27: "can I copy and paste the elements"): the element's own markup as the
 * SOURCE holds it, marks and hand values included, scrubbed ({@link scrubCopy}); its words, for another app;
 * its name tag in the document it came from, to find it there again; and whether it was one the user added.
 */
export interface CopiedElement {
  html: string
  text: string
  eid: string | null
  added: boolean
  kind?: AddedKind
}

/** A copy this long is not one element anyone means to paste: the whole slide's cap is 60,000 characters. */
const COPY_MAX = 200_000

const URL_ATTRS: ReadonlySet<string> = new Set(['href', 'src', 'xlink:href', 'action', 'formaction', 'poster'])

/**
 * What a copy never carries, on the way out and again on the way in (a copy can come back from storage):
 * scripts, event handlers, `javascript:` URLs, the bridge's live marks (`data-bds-*`) and a caret. A chart a
 * script draws therefore pastes empty; the script is the artifact's, not the element's.
 */
function scrubCopy(root: Element): void {
  for (const script of Array.from(root.querySelectorAll('script'))) script.remove()
  for (const el of [root, ...Array.from(root.querySelectorAll('*'))]) {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase()
      const drop =
        name.startsWith('on') ||
        name.startsWith('data-bds') ||
        name === 'contenteditable' ||
        (URL_ATTRS.has(name) && /^\s*javascript:/i.test(attr.value))
      if (drop) el.removeAttribute(attr.name)
    }
  }
}

/** An element's words as another app should get them: a line where a `<br>` breaks one, spaces collapsed. */
function wordsOf(el: Element): string {
  const clone = el.cloneNode(true) as Element
  for (const br of Array.from(clone.querySelectorAll('br'))) br.replaceWith(el.ownerDocument.createTextNode('\n'))
  return (clone.textContent ?? '').replace(/[ \t\f\v\r]+/g, ' ').replace(/ *\n */g, '\n').trim()
}

/**
 * The element at `path`, as a copy ({@link CopiedElement}). Refused (null): body, the slide or anything holding
 * it (measured when `slidePath` is given), anything out of a hand edit's reach, and a copy too long to be one
 * element. `html` never holds a script, a handler or a bridge mark.
 */
export function copyElementAt(html: string, path: number[], slidePath: number[] | null = null): CopiedElement | null {
  if (typeof html !== 'string' || !Array.isArray(path)) return null
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const el = resolveArtifactPath(doc, path)
    if (!el || isBody(el) || isTheSlide(el, measuredSlide(doc, slidePath)) || !isHandEditable(el)) return null
    const clone = el.cloneNode(true) as Element
    scrubCopy(clone)
    const markup = clone.outerHTML
    if (markup.length > COPY_MAX) return null
    const eid = el.getAttribute(EID_ATTR)
    const added = isHandAdded(el)
    return {
      html: markup,
      text: wordsOf(clone),
      eid: eid && EID_PATTERN.test(eid) ? eid : null,
      added,
      ...(added ? { kind: addedKindOf(el) } : {}),
    }
  } catch {
    return null
  }
}

/**
 * Where a copy goes:
 * - `after`: right after the element at that path, in its parent. In a layout the layout places it; a free
 *   element sits right above the one it follows (Beka, 2026-09-27).
 * - `at`: at that index in that path's parent, the place a cut element had.
 * - `into`: last in that element, or with null in the slide's own element ({@link addParentOf}).
 */
export type PastePlace = { after: number[] } | { at: number[] } | { into: number[] | null }

export interface PastedElement {
  /** The document with the copy in it. */
  html: string
  /** Where the copy sits in the returned document. */
  path: number[]
  /** Its parent, and the sibling it went before (null: last), in the document BEFORE the insert: the frame's. */
  parentPath: number[]
  beforePath: number[] | null
  /** The inserted element's own markup, fresh name tags and all: exactly what the frame inserts. */
  fragment: string
  /** The copy's own name tag, when the element it copies had one. */
  eid: string | null
}

/**
 * Puts a copy into the document. Every name tag in it is new, the root's included, so a copy is never taken
 * for its original: not by a hand edit that names one, and not by the keep after an AI turn. Its hand marks
 * and values stay; they are the copy's own hand edits from here on. `data-hand-add` stays on the root of a
 * copy of something the user added, and on nothing else: a copy of the artifact's own content is content.
 *
 * Refused (null): a copy that is not one element, a parent {@link moveHandTo} would refuse (words, a link, a
 * button, a `deck-*`, something added, body while a frame is the slide), a place next to the slide or inside
 * the element's own copy, and the same two checks on the result: the slide element unchanged (P4), and the same
 * tree through a re-parse (P3).
 */
export function pasteElementAt(
  html: string,
  copy: CopiedElement,
  place: PastePlace,
  slidePath: number[] | null = null,
): PastedElement | null {
  if (typeof html !== 'string' || !copy || typeof copy.html !== 'string' || copy.html.length > COPY_MAX || !place) return null
  const box: Omit<PastedElement, 'html'> & { shape: string | null } = {
    path: [],
    parentPath: [],
    beforePath: null,
    fragment: '',
    eid: null,
    shape: null,
  }
  const next = editArtifactDocument(html, (doc) => {
    const measured = measuredSlide(doc, slidePath)
    const slide = measured ?? slideElementOf(doc)
    let parent: Element | null = null
    let before: Element | null = null
    if ('after' in place) {
      const anchor = Array.isArray(place.after) ? resolveArtifactPath(doc, place.after) : null
      if (!anchor || isBody(anchor) || isTheSlide(anchor, measured)) return false
      parent = anchor.parentElement
      before = anchor.nextElementSibling
    } else if ('at' in place) {
      if (!Array.isArray(place.at) || place.at.length === 0) return false
      const index = place.at[place.at.length - 1]
      parent = resolvePath(doc, place.at.slice(0, -1))
      if (!parent || !Number.isInteger(index) || index < 0 || index > parent.children.length) return false
      before = parent.children[index] ?? null
    } else if ('into' in place) {
      parent = place.into === null ? addParentOf(doc) : Array.isArray(place.into) ? resolvePath(doc, place.into) : null
    }
    if (!parent || !canHoldMoved(parent) || (isBody(parent) && parent !== slide)) return false
    const holder = doc.createElement('template')
    holder.innerHTML = copy.html
    const nodes = Array.from(holder.content.childNodes).filter(
      (node) => node.nodeType !== Node.TEXT_NODE || (node.textContent ?? '').trim() !== '',
    )
    if (nodes.length !== 1 || nodes[0].nodeType !== Node.ELEMENT_NODE) return false
    const el = doc.importNode(nodes[0], true) as Element
    scrubCopy(el)
    if (!isHandEditable(el)) return false
    // Fresh name tags, counted up from the document's next one; the copy is not in the document yet.
    let n = Number(nextEid(doc).slice(1))
    for (const tagged of [el, ...Array.from(el.querySelectorAll(`[${EID_ATTR}]`))]) {
      if (tagged.hasAttribute(EID_ATTR)) tagged.setAttribute(EID_ATTR, `e${n++}`)
    }
    for (const inner of Array.from(el.querySelectorAll(`[${ADD_ATTR}]`))) inner.removeAttribute(ADD_ATTR)
    if (copy.added) el.setAttribute(ADD_ATTR, '')
    else el.removeAttribute(ADD_ATTR)
    const parentPath = pathOfElement(parent)
    const beforePath = before ? pathOfElement(before) : null
    if (!parentPath || (before && !beforePath)) return false
    parent.insertBefore(el, before)
    if (!measured && slideElementOf(doc) !== slide) return false
    const path = pathOfElement(el)
    if (!path) return false
    box.path = path
    box.parentPath = parentPath
    box.beforePath = beforePath
    box.fragment = el.outerHTML
    box.eid = el.getAttribute(EID_ATTR)
    box.shape = shapeOf(doc)
    return true
  })
  if (!next || box.shape === null) return null
  try {
    const again = new DOMParser().parseFromString(next, 'text/html')
    if (shapeOf(again) !== box.shape) return null
    if (box.eid !== null && resolveArtifactPath(again, box.path)?.getAttribute(EID_ATTR) !== box.eid) return null
  } catch {
    return null
  }
  return { html: next, path: box.path, parentPath: box.parentPath, beforePath: box.beforePath, fragment: box.fragment, eid: box.eid }
}

/**
 * Where the element a copy was taken from is now: by its name tag when it had one (and it is still the only one),
 * else at the path it was copied from, when what stands there is still that element (the same tag, words and
 * classes; a move or a delete puts something else there). Null: it is gone, or cannot be told apart.
 */
export function findOriginal(html: string, clip: { path: number[]; eid: string | null; html: string }): number[] | null {
  if (typeof html !== 'string' || !clip || !Array.isArray(clip.path)) return null
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    if (clip.eid && EID_PATTERN.test(clip.eid)) {
      const found = queryBodyAll(doc, `[${EID_ATTR}="${clip.eid}"]`)
      if (found.length === 1) return pathOfElement(found[0])
      if (found.length > 1) return null
    }
    const at = resolveArtifactPath(doc, clip.path)
    const holder = doc.createElement('template')
    holder.innerHTML = typeof clip.html === 'string' ? clip.html : ''
    const was = holder.content.firstElementChild
    if (!at || !was || at.tagName !== was.tagName) return null
    const same =
      (at.getAttribute('class') ?? '') === (was.getAttribute('class') ?? '') && wordsOf(at) === wordsOf(was)
    return same ? clip.path : null
  } catch {
    return null
  }
}

/** Which kind of added element sits at `path` ({@link addedKindOf}), or null for anything the user did not add. */
export function addedKindAt(html: string, path: number[]): AddedKind | null {
  if (typeof DOMParser !== 'function' || typeof html !== 'string' || !html.includes(ADD_ATTR)) return null
  try {
    const el = resolveArtifactPath(new DOMParser().parseFromString(html, 'text/html'), path)
    return el && !isBody(el) && isHandAdded(el) ? addedKindOf(el) : null
  } catch {
    return null
  }
}

/** Whether the element at `path` is one the user added. False for body, an unresolvable path, no DOM. */
export function isHandAddedAt(html: string, path: number[]): boolean {
  if (typeof DOMParser !== 'function' || typeof html !== 'string' || !html.includes(ADD_ATTR)) return false
  try {
    const el = resolveArtifactPath(new DOMParser().parseFromString(html, 'text/html'), path)
    return !!el && !isBody(el) && isHandAdded(el)
  } catch {
    return false
  }
}

/**
 * What Delete may take out (Beka, 2026-09-25: "we need to have delete functionality on the elements"): any
 * element a hand edit reaches, except the slide itself. Body and the frame on the slide chain would take
 * the whole slide with them. Inside a `deck-*` or an `svg` nothing is addressable in the source
 * ({@link isHandEditable}); the component or the svg itself goes like any other element.
 */
/**
 * The slide as the live frame MEASURED it (the element that fills the frame, `slideTarget`), when the host has
 * that answer: the source can only guess it by following single children down from body, and that guess
 * takes a lone 622px column beside a picture the user added for the slide itself. Null: guess, as before.
 */
function measuredSlide(doc: Document, slidePath: number[] | null | undefined): Element | null {
  if (!Array.isArray(slidePath)) return null
  return resolvePath(doc, slidePath)
}

/** The slide itself, or what holds it: nothing a hand may move or delete. */
function isTheSlide(el: Element, measured: Element | null): boolean {
  return measured ? el === measured || el.contains(measured) : isSlideElement(el)
}

function isRemovable(el: Element | null, measured: Element | null = null): el is Element {
  return !!el && !isBody(el) && !isTheSlide(el, measured) && isHandEditable(el)
}

/**
 * Deletes the element at `path`, whole: Delete on a selection, as one change that Undo brings back. `added`
 * says it was one the user added ({@link ADD_ATTR}), and `kind` which kind it was. Paths inside it are gone
 * and its later siblings' have moved up one. The live bridge refuses more (it can measure what fills the
 * frame, which the source cannot), so the host writes this only when the bridge removed the element too.
 */
export function removeHand(
  html: string,
  path: number[],
  slidePath: number[] | null = null,
): { html: string; added: boolean; kind?: AddedKind } | null {
  const box: { added: boolean; kind?: AddedKind } = { added: false }
  const next = editArtifactDocument(html, (doc) => {
    const el = resolveArtifactPath(doc, path)
    const measured = measuredSlide(doc, slidePath)
    if (!isRemovable(el, measured)) return false
    const slide = slideElementOf(doc)
    box.added = isHandAdded(el)
    if (box.added) box.kind = addedKindOf(el)
    el.remove()
    // The same slide (P4, as moveHandTo): deleting the headline beside a grid would leave the grid the frame's
    // only child, and the grid would become "the slide", which nothing can move or delete after. With the
    // frame's measured slide the question does not arise: what fills the frame is not what was deleted.
    return measured !== null || slideElementOf(doc) === slide
  })
  return next ? { html: next, added: box.added, ...(box.kind ? { kind: box.kind } : {}) } : null
}

/** Whether {@link removeHand} would delete the element at `path`. Writes nothing; false with no DOM. */
export function canRemoveAt(html: string, path: number[], slidePath: number[] | null = null): boolean {
  if (typeof DOMParser !== 'function' || typeof html !== 'string') return false
  try {
    return removeHand(html, path, slidePath) !== null
  } catch {
    return false
  }
}

/**
 * Takes out a picture the user added: {@link removeHand}, only when the element carries {@link ADD_ATTR}.
 * The image picker's Remove, which deletes nothing else.
 */
export function removeHandAdded(html: string, path: number[]): { html: string } | null {
  const out = removeHand(html, path)
  return out?.added ? { html: out.html } : null
}

/** A picture in the source, as the Properties panel's Image section reads it. */
export interface HandImage {
  /** The pool id (`data-deck-image`). */
  id: string
  /** The stored URL the source holds (the frame shows the same picture's bytes). */
  src: string
  alt: string
  /** The user added it by hand ({@link ADD_ATTR}): only then can the panel remove it. */
  added: boolean
}

/**
 * The picture at `path`: an `<img>` in the deck's own storage (`data-deck-image` and a stored URL). Null for
 * anything else, a picture from elsewhere included: those cannot be swapped for a deck picture without
 * breaking the validator's picture rule on the way.
 */
export function readImageAt(html: string, path: number[]): HandImage | null {
  if (typeof DOMParser !== 'function' || typeof html !== 'string' || !html.includes('data-deck-image')) return null
  try {
    const el = resolveArtifactPath(new DOMParser().parseFromString(html, 'text/html'), path)
    if (!el || el.tagName.toUpperCase() !== 'IMG') return null
    const id = el.getAttribute('data-deck-image') ?? ''
    const src = el.getAttribute('src') ?? ''
    if (!DECK_IMAGE_ID.test(id) || !isStoredImageUrl(src)) return null
    return { id, src, alt: el.getAttribute('alt') ?? '', added: isHandAdded(el) }
  } catch {
    return null
  }
}

/**
 * Every deck picture showing this exact stored URL, in document order, and whether the user added it. How a
 * picture placed before its upload answered is found again once it has (Beka, 2026-09-27: the picture shows at
 * once and uploads behind): by then the user may have moved it, or added more, so a path from the add would
 * be stale.
 */
export function findDeckImagePaths(html: string, src: string): { path: number[]; added: boolean }[] {
  if (typeof DOMParser !== 'function' || typeof html !== 'string' || !src || !html.includes(src)) return []
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const out: { path: number[]; added: boolean }[] = []
    for (const el of queryBodyAll(doc, 'img[data-deck-image]')) {
      if (el.getAttribute('src') !== src) continue
      const path = pathOfElement(el)
      if (path && path.length > 0) out.push({ path, added: isHandAdded(el) })
    }
    return out
  } catch {
    return []
  }
}

/**
 * Swaps the picture at `path` for another from the deck's storage (Beka, 2026-09-25: "to change the image"):
 * its pool id, its src and its alt, nothing else. The box stays where the user put it and the new picture
 * fills it the way the old one did (`object-fit`), as Figma keeps an image layer's frame when its image
 * changes. Only a picture {@link readImageAt} reads, and only to a stored URL or, for the live frame's
 * mirror, picture bytes.
 */
export function replaceHandImage(
  html: string,
  path: number[],
  image: { id: string; src: string; alt: string },
): { html: string } | null {
  if (!image || typeof image.id !== 'string' || !DECK_IMAGE_ID.test(image.id)) return null
  const src = typeof image.src === 'string' ? image.src.trim() : ''
  if (/[\s<>"]/.test(src) || !(isStoredImageUrl(src) || /^data:image\//i.test(src))) return null
  const next = editArtifactDocument(html, (doc) => {
    const el = resolveArtifactPath(doc, path)
    if (!el || el.tagName.toUpperCase() !== 'IMG' || !isHandEditable(el)) return false
    if (!DECK_IMAGE_ID.test(el.getAttribute('data-deck-image') ?? '')) return false
    if (el.getAttribute('data-deck-image') === image.id && el.getAttribute('src') === src) return false
    el.setAttribute('data-deck-image', image.id)
    el.setAttribute('src', src)
    el.setAttribute('alt', cleanAlt(image.alt))
    return true
  })
  return next ? { html: next } : null
}

/** The marks on one element of the source: its name tag and what the user set. Null when unresolvable. */
export function readHandAt(html: string, target: HandTarget): { eid: string | null; hand: Map<string, string> } | null {
  if (typeof DOMParser !== 'function' || typeof html !== 'string') return null
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    if (!doc.body) return null
    const el = resolveTarget(doc, target)
    if (!el) return null
    const eid = el.getAttribute(EID_ATTR)
    return { eid: eid && EID_PATTERN.test(eid) ? eid : null, hand: handOf(el) }
  } catch {
    return null
  }
}

/**
 * The name tag a message about the element at `path` points at: its own, or the nearest hand-edited
 * ancestor's, the same walk a pin's hit makes (`closest('[data-eid]')`), up to and including body (the
 * empty path is body itself). Null when nothing up the chain was ever hand-edited. The slide's own tag is
 * the caller's to set aside when the element is only ON the slide (`useInlineTextEdit.selectedEid`): only
 * the bridge knows which element that is.
 */
export function closestHandEid(html: string, path: number[]): string | null {
  if (typeof DOMParser !== 'function' || typeof html !== 'string' || !html.includes(EID_ATTR)) return null
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const hit = resolvePath(doc, path)?.closest(`[${EID_ATTR}]`) ?? null
    // `closest` would go on to `<html>`, which no hand edit reaches: a tag there is no one's.
    const eid = hit && doc.body?.contains(hit) ? hit.getAttribute(EID_ATTR) : null
    return eid && EID_PATTERN.test(eid) ? eid : null
  } catch {
    return null
  }
}

/** Words cut to a label: URLs out, whitespace folded, at most 32 characters. */
function shortLabel(text: string): string {
  const flat = text.replace(/https?:\/\/\S+/g, '').replace(/\s+/g, ' ').trim()
  return flat.length > 32 ? `${flat.slice(0, 31)}…` : flat
}

/**
 * A short, URL-free label for prompts and notices: the element's own words, or its first class. Body, and
 * the frame on the slide chain, are "the slide": their words are every word on it, which would name
 * nothing. A picture has no words of its own, so its alt text speaks for it.
 */
export function handLabel(el: Element): string {
  if (isSlideElement(el)) return 'the slide'
  if (el.tagName.toUpperCase() === 'IMG') return shortLabel(el.getAttribute('alt') ?? '') || 'image'
  const text = shortLabel(el.textContent ?? '')
  if (text) return text
  // A circle or a square the user drew has no words and no class: what it is names it.
  if (isHandAdded(el)) return 'shape'
  const cls = (el.getAttribute('class') ?? '').trim().split(/\s+/)[0]
  return cls ? `.${cls}` : el.tagName.toLowerCase()
}

/** Every hand-edited element in the document, in document order. Empty for no DOM or no edits. */
export function listHandEdits(html: string): HandEditEntry[] {
  if (typeof DOMParser !== 'function' || typeof html !== 'string' || !html.includes(HAND_ATTR)) return []
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const out: HandEditEntry[] = []
    queryBodyAll(doc, `[${HAND_ATTR}]`).forEach((el) => {
      const eid = el.getAttribute(EID_ATTR) ?? ''
      const props = [...handOf(el).keys()]
      if (!EID_PATTERN.test(eid) || props.length === 0) return
      out.push({ eid, tag: el.tagName.toLowerCase(), label: handLabel(el), props, ...(isHandAdded(el) ? { added: true } : {}) })
    })
    return out
  } catch {
    return []
  }
}

/**
 * The same document with every hand mark taken off: `data-eid`, `data-hand`, and each inline declaration
 * the mark lists. For HTML that leaves its own artifact, as a reference or a style exemplar, or as the
 * input to a re-roll: a copy is the AI's, not the user's, and a copied mark would put a detached copy
 * exactly on top of the original. A picture the user added goes whole: it is theirs, and stripped of its
 * marks it would be an unplaced, natural-size picture in the copy. Returns the input unchanged when there
 * is nothing to strip or no DOM.
 */
export function stripHandMarks(html: string): string {
  if (typeof html !== 'string' || (!html.includes(EID_ATTR) && !html.includes(HAND_ATTR))) return html
  const next = editArtifactDocument(html, (doc) => {
    let touched = false
    queryBodyAll(doc, `[${ADD_ATTR}]`).forEach((el) => {
      if (!isBody(el)) el.remove()
      touched = true
    })
    // Whatever still wears the mark (body, or a root no hand edit reaches) keeps itself and loses the mark.
    doc.querySelectorAll(`[${ADD_ATTR}]`).forEach((el) => el.removeAttribute(ADD_ATTR))
    queryBodyAll(doc, `[${EID_ATTR}], [${HAND_ATTR}]`).forEach((el) => {
      const style = inlineStyleOf(el)
      for (const name of parseHand(el.getAttribute(HAND_ATTR)).keys()) style?.removeProperty(name)
      el.removeAttribute(HAND_ATTR)
      el.removeAttribute(EID_ATTR)
      dropHusk(el)
      touched = true
    })
    return touched
  })
  return next ?? html
}
